import {
    type ReactNode,
    type RefObject,
    useEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
    PANEL_WINDOW_INSET_PX,
    type PanelPosition,
    panelPositionOpening,
} from '~/components/toolbar/shared/panelPlacement';
import PanelFrame from '~/components/toolbar/shared/PanelFrame';
import PanelHeader from '~/components/toolbar/shared/PanelHeader';
import { useToolRailSlot } from '~/components/toolbar/shared/ToolRailSlots';
import {
    useKeptInWindow,
    usePanelDrag,
} from '~/components/toolbar/shared/usePanelDrag';

interface DraggablePanelProps {
    /** Whether the panel shows. Keep it mounted to keep its place. */
    open: boolean;
    title: string;
    /** Width of the panel, in CSS pixels. */
    width: number;
    /** The control the panel first opens beside. */
    anchorRef: RefObject<HTMLElement | null>;
    onClose: () => void;
    children: ReactNode;
}

/**
 * A panel over the views that the user moves by its title, anywhere in the
 * window, as the tool options dock moves when detached. It first opens
 * beside its anchor, and then where the user left it, kept inside the
 * window as the window and the panel resize. It renders into the dock
 * layer, so it shares the dock's place among the layers, and does nothing
 * without one.
 */
export default function DraggablePanel({
    open,
    title,
    width,
    anchorRef,
    onClose,
    children,
}: DraggablePanelProps) {
    const host = useToolRailSlot('panels');
    const panelRef = useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = useState<PanelPosition | null>(null);
    const dragListeners = usePanelDrag(panelRef, setPosition);
    useKeptInWindow(panelRef, host && open ? position : null, setPosition);

    useEffect(() => {
        if (open && !position) {
            setPosition(panelPositionOpening(anchorRef.current, width));
        }
    }, [anchorRef, open, position, width]);

    if (!host || !open || !position) {
        return null;
    }
    return createPortal(
        <PanelFrame
            ref={panelRef}
            role="group"
            aria-label={title}
            sx={{
                position: 'fixed',
                left: position.x,
                top: position.y,
                width,
                maxWidth: `calc(100vw - ${2 * PANEL_WINDOW_INSET_PX}px)`,
                maxHeight: `calc(100vh - ${2 * PANEL_WINDOW_INSET_PX}px)`,
            }}
            header={
                <PanelHeader
                    title={title}
                    dragListeners={dragListeners}
                    onClose={onClose}
                />
            }
        >
            {children}
        </PanelFrame>,
        host
    );
}
